import {useCallback} from 'react'
import {useDispatch} from 'react-redux'
import {oktaAuth} from '@/config/oktaConfig'
import {clearOktaTokens, clearAllAuthData} from '@/utils/authUtils'
import {clearSelectedTeam} from '@/store/slices/team/teamSlice'
import {clearPrograms} from '@/store/slices/program/programSlice'
import {persistor} from '@/store'

/**
 * Enhanced logout hook inspired by B2B Portal's robust logout implementation
 * Handles comprehensive cleanup and prevents race conditions
 */
export const useLogout = () => {
  const dispatch = useDispatch()

  /**
   * Performs comprehensive cleanup tasks before logout
   * Similar to B2B Portal's performCleanUpTasks
   */
  const performCleanUpTasks = useCallback(async (): Promise<void> => {
    try {
      // Clear Redux state
      dispatch(clearSelectedTeam())
      dispatch(clearPrograms())

      // Purge Redux persist store - handle the promise properly
      await persistor.purge()
    } catch (error) {
      // Continue with logout even if cleanup fails
    }
  }, [dispatch])

  /**
   * Clears localStorage systematically like B2B Portal
   */
  const clearLocalStorage = useCallback((): void => {
    try {
      // Clear authentication tokens
      clearAllAuthData()

      // Clear any application-specific data
      localStorage.removeItem('selectedTeam')
      localStorage.removeItem('selectedProgram')
      localStorage.removeItem('userPreferences')

      // Clear any cached data
      localStorage.removeItem('apiCache')
      localStorage.removeItem('lastSyncTime')
    } catch (error) {
      // Error handling is silent
    }
  }, [])

  /**
   * Main logout function with comprehensive cleanup
   * Inspired by B2B Portal's handleLogOut
   */
  const handleLogOut = useCallback(async (): Promise<void> => {
    // Prevent multiple logout attempts
    if ((globalThis as any).logoutInProgress) {
      return
    }

    ;(globalThis as any).logoutInProgress = true

    try {
      console.log('Starting logout process...')

      // Step 1: Perform cleanup tasks
      await performCleanUpTasks()

      // Step 2: Clear localStorage
      clearLocalStorage()

      // Step 3: Revoke tokens at Okta server
      try {
        await oktaAuth.revokeAccessToken()
        await oktaAuth.revokeRefreshToken()
      } catch (revokeError) {
        console.warn('Token revocation failed:', revokeError)
      }

      // Step 4: Clear tokens from storage
      await oktaAuth.tokenManager.clear()
      clearOktaTokens()

      // Step 5: Close Okta session (this clears the Okta session cookie)
      try {
        await oktaAuth.closeSession()
      } catch (sessionError) {
        console.warn('Close session failed:', sessionError)
      }

      // Step 6: Clear session flag
      ;(globalThis as any).session401Flag = false

      console.log('Logout completed successfully')

      // Step 7: Set logout flag and redirect to login with forced prompt
      sessionStorage.setItem('justLoggedOut', 'true')
      window.location.replace('/login')
    } catch (error) {
      console.error('Error during logout:', error)

      // Fallback: ensure cleanup even if Okta logout fails
      clearLocalStorage()
      clearOktaTokens()

      // Set logout flag for Login.tsx to detect
      sessionStorage.setItem('justLoggedOut', 'true')

      // Force redirect to login
      window.location.replace('/login')
    } finally {
      ;(globalThis as any).logoutInProgress = false
    }
  }, [performCleanUpTasks, clearLocalStorage])

  /**
   * Force logout without Okta signOut (for 401 errors)
   * Similar to B2B Portal's logoutUserForcefully
   */
  const logoutUserForcefully = useCallback(async (): Promise<void> => {
    console.log('Force logout triggered')

    // Perform cleanup without Okta signOut
    await performCleanUpTasks()
    clearLocalStorage()

    // Clear session flag
    ;(globalThis as any).session401Flag = false

    // Redirect to login (like B2B Portal)
    window.location.replace('/login')
  }, [performCleanUpTasks, clearLocalStorage])

  return {
    handleLogOut,
    logoutUserForcefully,
    performCleanUpTasks,
    clearLocalStorage,
  }
}
